import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { PageMetadata } from '@/components/common/PageMetadata'
import { useAuthStore } from '@/stores/useAuthStore'
import { SignupForm } from '@/features/auth/components/SignupForm'
import { AuthLayout, authLinkClass } from '@/features/auth/components/AuthLayout'
import { SignupShowcase } from '@/features/auth/components/SignupShowcase'
import { EMPTY_SIGNUP_PREVIEW } from '@/features/auth/types/signupPreview'

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'Kaamco'

/**
 * Organization signup page. Redirects to dashboard if already authenticated.
 * Shares the sign-in shell; the brand panel previews the workspace as it is typed.
 */
export function SignupPage(): React.ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [preview, setPreview] = useState(EMPTY_SIGNUP_PREVIEW)

  // Strip any credentials that may have leaked into the URL (e.g. from a
  // native GET form submit before React attached its handlers).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const sensitive = ['password', 'confirmPassword', 'email', 'organizationSlug', 'organizationName']
    let dirty = false
    for (const key of sensitive) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key)
        dirty = true
      }
    }
    if (dirty) {
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash)
    }
  }, [])

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  const signInLink = (
    <>
      Already have an organization?{' '}
      <Link to="/login" className={authLinkClass}>
        Sign in
      </Link>
    </>
  )

  return (
    <AuthLayout
      aside={<SignupShowcase preview={preview} />}
      headerAction={signInLink}
      panelClassName="max-w-[34rem]"
    >
      <PageMetadata />
      <h1 className="font-display text-[2rem] font-semibold leading-[1.1] tracking-[-0.035em] text-balance sm:text-[2.25rem]">
        Create your organization
      </h1>
      <p className="mt-2 text-[0.9375rem] text-[var(--public-muted)]">
        Set up a {APP_NAME} workspace for your team. You&rsquo;ll be its owner admin.
      </p>
      <div className="mt-8">
        <SignupForm onPreviewChange={setPreview} />
      </div>
      <p className="mt-8 text-sm text-[var(--public-muted)] sm:hidden lg:block xl:hidden">{signInLink}</p>
    </AuthLayout>
  )
}
